import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { contactsApi } from 'services/contacts';
import { listsTreeApi } from 'services/listsTree';
import {
  selectIsPolling,
  selectExpectedCountDelta,
  selectBaseCount,
  setBaseCount,
  syncComplete,
  incrementAttempts,
  POLL_INTERVAL,
} from 'features/findemSync';

export const useFindemSyncManager = () => {
  const dispatch = useDispatch();
  const isPolling = useSelector(selectIsPolling);
  const expectedDelta = useSelector(selectExpectedCountDelta);
  const baseCount = useSelector(selectBaseCount);
  const timerRef = useRef(null);
  const baseCountRef = useRef(baseCount);
  const expectedDeltaRef = useRef(expectedDelta);
  const wasPollingRef = useRef(false);

  useEffect(() => {
    baseCountRef.current = baseCount;
  }, [baseCount]);

  useEffect(() => {
    expectedDeltaRef.current = expectedDelta;
  }, [expectedDelta]);

  const checkCount = useCallback(async () => {
    try {
      const result = await dispatch(
        contactsApi.endpoints.getSharedLists.initiate({ countOnly: true }, { forceRefetch: true }),
      );
      const currentCount = result?.data?.meta?.sharedListTotal;

      if (currentCount == null) return;

      if (baseCountRef.current === null) {
        dispatch(setBaseCount(currentCount));
        return;
      }

      const expectedCount = baseCountRef.current + expectedDeltaRef.current;
      if (currentCount === expectedCount) {
        dispatch(syncComplete());
      } else {
        dispatch(incrementAttempts());
      }
    } catch {
      dispatch(incrementAttempts());
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isPolling) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (wasPollingRef.current) {
        wasPollingRef.current = false;
        dispatch(contactsApi.util.invalidateTags(['contacts-shared-lists']));
        dispatch(listsTreeApi.util.invalidateTags(['list-tree']));
      }

      return undefined;
    }

    wasPollingRef.current = true;
    checkCount();

    timerRef.current = setInterval(checkCount, POLL_INTERVAL);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPolling, checkCount, dispatch]);
};
